import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CheckCircleIcon,
  PauseIcon,
  PlayIcon,
  TrashIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { useMessageBox } from '../../contexts/MessageBoxContext';
import { PlanningStatusBadge } from '../strategic-planning/PlanningStatusBadge';
import {
  TasksCalendarTaskListItem,
  TasksCalendarTaskStatusFilter,
  TasksCalendarUser,
  cancelTasksCalendarTask,
  deleteTasksCalendarTask,
  listTasksCalendarTasksForPanel,
  listTasksCalendarUsers,
  pauseTasksCalendarTask,
  startTasksCalendarTask,
} from '../../services/tasksCalendarApi';
import { formatDateBr } from '../../utils/formatDateBr';
import { getRequestErrorMessage } from '../../utils/getRequestErrorMessage';

function toIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getDefaultDateRange(): { from: string; to: string } {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), 1);
  const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { from: toIsoDate(from), to: toIsoDate(to) };
}

const STATUS_FILTER_OPTIONS: Array<{ value: '' | TasksCalendarTaskStatusFilter; label: string }> = [
  { value: '', label: 'Todos os status' },
  { value: 'Planned', label: 'Planejada' },
  { value: 'InProgress', label: 'Em andamento' },
  { value: 'Overdue', label: 'Atrasada' },
  { value: 'Completed', label: 'Concluída' },
  { value: 'Cancelled', label: 'Cancelada' },
];

export function TopHeaderTasksPanel() {
  const { confirmDelete } = useMessageBox();
  const defaultRange = getDefaultDateRange();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<TasksCalendarUser[]>([]);
  const [visibleUserIds, setVisibleUserIds] = useState<Set<string>>(new Set());
  const [fromDate, setFromDate] = useState(defaultRange.from);
  const [toDate, setToDate] = useState(defaultRange.to);
  const [statusFilter, setStatusFilter] = useState<'' | TasksCalendarTaskStatusFilter>('');
  const [cycleName, setCycleName] = useState<string | null>(null);
  const [items, setItems] = useState<TasksCalendarTaskListItem[]>([]);
  const [cancelTaskId, setCancelTaskId] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [actionTaskId, setActionTaskId] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const hasSubordinates = users.some((user) => user.isSubordinate);
  const pendingCount = items.filter(
    (item) => item.status === 'Planned' || item.status === 'InProgress' || item.status === 'Overdue',
  ).length;

  const loadUsers = useCallback(async (): Promise<void> => {
    try {
      const result = await listTasksCalendarUsers();
      setUsers(result);
      setVisibleUserIds((current) => {
        if (current.size > 0) {
          return current;
        }
        return new Set(result.map((user) => user.userId));
      });
    } catch {
      setUsers([]);
    }
  }, []);

  const loadTasks = useCallback(async (): Promise<void> => {
    if (visibleUserIds.size === 0) {
      setItems([]);
      setCycleName(null);
      return;
    }

    setLoading(true);
    try {
      const result = await listTasksCalendarTasksForPanel({
        from: fromDate,
        to: toDate,
        userIds: Array.from(visibleUserIds),
        status: statusFilter || undefined,
      });
      setItems(result.items);
      setCycleName(result.currentCycleName ?? null);
    } catch (error) {
      setItems([]);
      setCycleName(null);
      toast.error(getRequestErrorMessage(error, 'Falha ao carregar tarefas.'));
    } finally {
      setLoading(false);
    }
  }, [fromDate, statusFilter, toDate, visibleUserIds]);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (visibleUserIds.size === 0) {
      return;
    }
    void loadTasks();
  }, [fromDate, loadTasks, statusFilter, toDate, visibleUserIds]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent): void {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
        setCancelTaskId(null);
        setCancelReason('');
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function toggleUserVisibility(userId: string): void {
    setVisibleUserIds((current) => {
      const next = new Set(current);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  }

  async function handleToggle(): Promise<void> {
    const next = !open;
    setOpen(next);
    if (!next) {
      setCancelTaskId(null);
      setCancelReason('');
    }
  }

  async function runTaskAction(
    taskId: string,
    action: () => Promise<unknown>,
    successMessage: string,
    fallbackError: string,
  ): Promise<void> {
    setActionTaskId(taskId);
    try {
      await action();
      toast.success(successMessage);
      await loadTasks();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, fallbackError));
    } finally {
      setActionTaskId(null);
    }
  }

  async function handleStart(taskId: string): Promise<void> {
    await runTaskAction(
      taskId,
      () => startTasksCalendarTask(taskId),
      'Tarefa iniciada.',
      'Falha ao iniciar tarefa.',
    );
  }

  async function handlePause(taskId: string): Promise<void> {
    await runTaskAction(
      taskId,
      () => pauseTasksCalendarTask(taskId),
      'Tarefa pausada.',
      'Falha ao pausar tarefa.',
    );
  }

  async function handleDelete(taskId: string): Promise<void> {
    if (
      !(await confirmDelete({
        title: 'Excluir tarefa',
        message: 'Excluir definitivamente esta tarefa? Esta ação não pode ser desfeita.',
      }))
    ) {
      return;
    }

    await runTaskAction(
      taskId,
      () => deleteTasksCalendarTask(taskId),
      'Tarefa excluída.',
      'Falha ao excluir tarefa.',
    );
  }

  async function handleConfirmCancel(taskId: string): Promise<void> {
    if (!cancelReason.trim()) {
      toast.error('Informe o motivo do cancelamento.');
      return;
    }

    await runTaskAction(
      taskId,
      () => cancelTasksCalendarTask(taskId, cancelReason.trim()),
      'Tarefa cancelada.',
      'Falha ao cancelar tarefa.',
    );
    setCancelTaskId(null);
    setCancelReason('');
  }

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        className="relative rounded-xl p-2 text-primary-700 hover:bg-primary-500/10"
        title="Minhas tarefas"
        aria-label="Minhas tarefas"
        onClick={() => void handleToggle()}
      >
        <CheckCircleIcon className="h-6 w-6" />
        {pendingCount > 0 ? (
          <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-400 px-1 text-[10px] font-bold text-primary-900">
            {pendingCount > 9 ? '9+' : pendingCount}
          </span>
        ) : null}
      </button>

      {open ? (
        <div className="absolute right-0 z-30 mt-2 w-[min(42rem,calc(100vw-2rem))] rounded-2xl border border-secondary-200 bg-white text-secondary-800 shadow-lg">
          <div className="border-b border-secondary-100 px-4 py-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-secondary-900">Minhas tarefas</h3>
                {cycleName ? (
                  <p className="mt-0.5 text-xs text-secondary-500">Ciclo: {cycleName}</p>
                ) : (
                  <p className="mt-0.5 text-xs text-secondary-500">
                    Nenhum ciclo de planejamento para o período selecionado.
                  </p>
                )}
              </div>
              <button
                type="button"
                className="erp-btn-secondary erp-btn-sm"
                onClick={() => void loadTasks()}
              >
                Atualizar
              </button>
            </div>

            <div className="mt-3 grid gap-2 sm:grid-cols-3">
              <label className="block text-xs">
                <span className="mb-1 block text-secondary-500">Data inicial</span>
                <input
                  type="date"
                  className="erp-input w-full py-1.5 text-sm"
                  value={fromDate}
                  onChange={(event) => setFromDate(event.target.value)}
                />
              </label>
              <label className="block text-xs">
                <span className="mb-1 block text-secondary-500">Data final</span>
                <input
                  type="date"
                  className="erp-input w-full py-1.5 text-sm"
                  value={toDate}
                  onChange={(event) => setToDate(event.target.value)}
                />
              </label>
              <label className="block text-xs">
                <span className="mb-1 block text-secondary-500">Status</span>
                <select
                  className="erp-input w-full py-1.5 text-sm"
                  value={statusFilter}
                  onChange={(event) =>
                    setStatusFilter(event.target.value as '' | TasksCalendarTaskStatusFilter)
                  }
                >
                  {STATUS_FILTER_OPTIONS.map((option) => (
                    <option key={option.value || 'all'} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {hasSubordinates ? (
              <div className="mt-3">
                <p className="mb-1.5 text-xs font-medium text-secondary-600">Colaboradores</p>
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  {users.map((user) => (
                    <label key={user.userId} className="inline-flex items-center gap-1.5 text-xs">
                      <input
                        type="checkbox"
                        checked={visibleUserIds.has(user.userId)}
                        onChange={() => toggleUserVisibility(user.userId)}
                      />
                      <span>{user.isSelf ? `${user.name} (eu)` : user.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          <div className="max-h-[28rem] overflow-y-auto">
            {loading ? (
              <p className="px-4 py-6 text-sm text-secondary-500">Carregando...</p>
            ) : items.length === 0 ? (
              <p className="px-4 py-6 text-sm text-secondary-500">Nenhuma tarefa encontrada.</p>
            ) : (
              items.map((task) => (
                <div
                  key={task.id}
                  className="border-b border-secondary-50 px-4 py-3 last:border-b-0"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-semibold text-secondary-900">{task.title}</p>
                        <PlanningStatusBadge scope="workItem" status={task.status} />
                      </div>
                      {task.descriptionPreview ? (
                        <p className="mt-1 line-clamp-2 text-sm text-secondary-600">
                          {task.descriptionPreview}
                        </p>
                      ) : null}
                      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-secondary-500">
                        {task.ownerUserName && hasSubordinates ? (
                          <span>Responsável: {task.ownerUserName}</span>
                        ) : null}
                        <span>Início: {formatDateBr(task.plannedStartDate)}</span>
                        <span>Prazo: {formatDateBr(task.plannedEndDate)}</span>
                      </div>
                    </div>

                    <div className="flex shrink-0 items-center gap-1">
                      {task.canStart ? (
                        <button
                          type="button"
                          className="erp-btn-primary erp-btn-sm gap-1"
                          title="Iniciar"
                          disabled={actionTaskId === task.id}
                          onClick={() => void handleStart(task.id)}
                        >
                          <PlayIcon className="h-4 w-4" />
                          Iniciar
                        </button>
                      ) : null}
                      {task.canPause ? (
                        <button
                          type="button"
                          className="rounded-lg p-1.5 text-amber-600 hover:bg-amber-50 disabled:opacity-50"
                          title="Pausar tarefa"
                          aria-label="Pausar tarefa"
                          disabled={actionTaskId === task.id}
                          onClick={() => void handlePause(task.id)}
                        >
                          <PauseIcon className="h-5 w-5" />
                        </button>
                      ) : null}
                      {task.canCancel ? (
                        <button
                          type="button"
                          className="rounded-lg p-1.5 text-rose-600 hover:bg-rose-50 disabled:opacity-50"
                          title="Cancelar tarefa"
                          aria-label="Cancelar tarefa"
                          disabled={actionTaskId === task.id}
                          onClick={() => {
                            setCancelTaskId(task.id);
                            setCancelReason('');
                          }}
                        >
                          <XCircleIcon className="h-5 w-5" />
                        </button>
                      ) : null}
                      {task.canDelete ? (
                        <button
                          type="button"
                          className="rounded-lg p-1.5 text-secondary-600 hover:bg-secondary-100 disabled:opacity-50"
                          title="Excluir definitivamente"
                          aria-label="Excluir definitivamente"
                          disabled={actionTaskId === task.id}
                          onClick={() => void handleDelete(task.id)}
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      ) : null}
                    </div>
                  </div>

                  {cancelTaskId === task.id ? (
                    <div className="mt-3 rounded-xl border border-rose-100 bg-rose-50/40 p-3">
                      <label className="block text-xs">
                        <span className="mb-1 block font-medium text-secondary-700">
                          Motivo do cancelamento
                        </span>
                        <textarea
                          className="erp-input min-h-[72px] w-full text-sm"
                          value={cancelReason}
                          onChange={(event) => setCancelReason(event.target.value)}
                          maxLength={2000}
                          placeholder="Descreva por que esta tarefa está sendo cancelada."
                        />
                      </label>
                      <div className="erp-btn-actions mt-2">
                        <button
                          type="button"
                          className="erp-btn-secondary erp-btn-sm"
                          onClick={() => {
                            setCancelTaskId(null);
                            setCancelReason('');
                          }}
                        >
                          Voltar
                        </button>
                        <button
                          type="button"
                          className="erp-btn-danger erp-btn-sm"
                          disabled={actionTaskId === task.id}
                          onClick={() => void handleConfirmCancel(task.id)}
                        >
                          Confirmar cancelamento
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
